import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatDefaultView, ChatWidget } from '@hevoio/hevo-ui-toolkit';
import { useObservableState } from '../../../../core/hooks/useObservableState';
import useService from '../../../../injector/hooks/useService';
import { getUnreadDisplayCount } from '../../../../core/utils/string';
import { TRACKER_SUPPORT_CTA_CLICK } from '../../../../core/constants/constants';
import { EnvConfig } from '../../../../core/env-config';
import { SupportService } from '../../../../support/interfaces/support';
import { TeamService } from '../../../../core/services/team.service';
import useTeamSettings from '../../../../containers/team/hooks/useTeamSettingsService';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import {
  makeCerebraAIConfig,
  makeCerebraStorage,
  makeCerebraZendeskConfig,
} from '../../chat-widget/CerebraAPI';
import { ChatbotEventTracker, ChatbotTracking } from '../../chat-widget/chatbot-tracking';
import { AppbarSupportCTA } from './AppbarSupportCTA';
import { AppbarSupportCTAWithMenu } from './AppbarSupportCTAWithMenu';

export function AppbarSupportBtnWrapper({ expanded = false }: { expanded?: boolean }) {
  const teamDetailsService = useService(TeamService);
  const supportService = useService(SupportService);

  const isTeamInTrial = teamDetailsService.teamDetails?.status === 'TRIAL';
  const [supportUnreadCount] = useObservableState(supportService.unreadCount$);
  const [canShowOnCallSupportIndicator] = useObservableState(
    supportService.isOnCallSupportAvailable$
  );
  const [supportPersonImageURL] = useObservableState(supportService.onCallSupportPersonImage$);
  // Show the chatbot only when the deployment build flag is on AND the per-user enabled
  // API (resolved once on login, published on chatbotEnabled$) says so.
  const { isCerebraEnabled } = useTeamSettings();
  const [isChatbotApiEnabled] = useObservableState(supportService.chatbotEnabled$);
  const isChatbotEnabled = isCerebraEnabled && isChatbotApiEnabled;

  // The widget is always mounted (when chatbot is enabled) as a FAB; `minimized`
  // is its controlled state. Both the appbar CTA and the widget's own FAB expand it.
  const [minimized, setMinimized] = useState(true);

  // useAnalyticsTracker returns a fresh object each render. Keep the latest in a
  // ref so `track` can stay referentially stable — the config factories are
  // memoized and the widget reads them from a ref, so a changing tracker identity
  // must not force them to be rebuilt.
  const analyticsTracker = useAnalyticsTracker();
  const analyticsTrackerRef = useRef(analyticsTracker);
  analyticsTrackerRef.current = analyticsTracker;
  const track = useCallback<ChatbotEventTracker>((event, properties) => {
    analyticsTrackerRef.current.eventTrack({ action: event, properties });
  }, []);

  const storage = useMemo(() => makeCerebraStorage(), []);
  const aiConfig = useMemo(() => makeCerebraAIConfig(track), [track]);
  const zendeskConfig = useMemo(() => makeCerebraZendeskConfig(track), [track]);

  const handleChatOpen = () => {
    if (isChatbotEnabled) {
      setMinimized(false);
      track(ChatbotTracking.TRACKER_CHATBOT_OPENED);
    } else {
      supportService.show();
    }
  };

  // Source/citation link clicks have no toolkit callback, so we delegate a click
  // listener on the container we own. The widget renders each source as
  // `<a class="_sourceLink_<hash>">`; CSS-modules keep the local name "sourceLink"
  // as a substring across rebuilds, so `a[class*="sourceLink"]` is a stable match.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!isChatbotEnabled || !el) return undefined;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.<HTMLAnchorElement>('a[class*="sourceLink"]');
      if (!anchor) return;
      let host = '';
      try {
        host = new URL(anchor.href).host;
      } catch {
        // ignore unparseable hrefs
      }
      track(ChatbotTracking.TRACKER_CHATBOT_SOURCE_CLICKED, host ? { host } : {});
    };

    el.addEventListener('click', onClick, true);
    return () => el.removeEventListener('click', onClick, true);
  }, [isChatbotEnabled, track]);

  return (
    <>
      {isTeamInTrial || !EnvConfig.isCombinedSupportOptionsEnabled() ? (
        <AppbarSupportCTA
          label='Live Chat'
          badgeContent={supportUnreadCount ? getUnreadDisplayCount(supportUnreadCount) : undefined}
          showOnCallSupportIndicator={canShowOnCallSupportIndicator}
          supportPersonImageURL={supportPersonImageURL}
          trackingEvent={TRACKER_SUPPORT_CTA_CLICK}
          expanded={expanded}
          onClick={handleChatOpen}
        />
      ) : (
        <AppbarSupportCTAWithMenu
          supportUnreadCount={supportUnreadCount}
          canShowOnCallSupportIndicator={canShowOnCallSupportIndicator}
          expanded={expanded}
          onChatOpen={handleChatOpen}
        />
      )}

      {isChatbotEnabled && (
        <div
          ref={containerRef}
          style={{ position: 'fixed', bottom: '16px', right: '16px', zIndex: 9998 }}
        >
          <ChatWidget
            title='Hevo Support'
            dataId='hevo-support-cerebra'
            ai={aiConfig}
            zendesk={zendeskConfig}
            storage={storage}
            height={630}
            showMinimize
            showNewChat
            showHistory
            showSupport
            isMinimized={minimized}
            onMinimizedChange={setMinimized}
            showSessionTitle={false}
            pollInterval={10000}
            maxAttachmentSize={25*1024*1024} // 25 MB
            defaultView={ChatDefaultView.History}
            notificationSound = {false}
            onNewChat={() => {
              track(ChatbotTracking.TRACKER_CHATBOT_NEW_CHAT);
              storage.clearSession();
            }}
          />
        </div>
      )}
    </>
  );
}
